/**
 * Lightweight main-thread responsiveness monitor.
 *
 * Chrome's "Page Unresponsive" dialog fires when the main JS thread is blocked
 * synchronously for several seconds. That dialog cannot be observed from JS, so
 * we use two proxies that report through the existing tracking pipeline:
 *
 *  1. Drift watchdog — a ~1s interval that measures how late each tick fires.
 *     A large gap means the main thread was frozen for ~that long.
 *  2. PerformanceObserver('longtask') — attributes long synchronous tasks to a
 *     script/frame so we can tell *which* code blocked.
 *
 * No new dependencies. Events are emitted via trackCustomEvent, which already
 * skips emission when tracking is disabled (no auth token) and auto-attaches
 * route + device context.
 */

import { trackCustomEvent } from "./index";

const TICK_MS = 1000;
// Report a stall only when the thread was frozen clearly longer than the tick.
const STALL_THRESHOLD_MS = 3000;
// Only report meaningfully long tasks, and rate-limit them so a busy page
// cannot flood the tracking queue.
const LONGTASK_REPORT_MS = 600;
const LONGTASK_MIN_GAP_MS = 5000;

let initialized = false;

const emitPerformanceEvent = (action, metadata) => {
  try {
    trackCustomEvent({
      action,
      eventType: "performance",
      metadata,
    });
  } catch {
    // Never let monitoring break the app.
  }
};

const startDriftWatchdog = () => {
  let expected = performance.now() + TICK_MS;

  // Backgrounded tabs throttle timers, producing huge fake gaps. Re-baseline
  // when the tab regains focus so we don't report false stalls.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      expected = performance.now() + TICK_MS;
    }
  });

  window.setInterval(() => {
    if (document.hidden) {
      expected = performance.now() + TICK_MS;
      return;
    }

    const now = performance.now();
    const drift = now - expected;
    expected = now + TICK_MS;

    if (drift >= STALL_THRESHOLD_MS) {
      emitPerformanceEvent("stall", {
        stall_ms: Math.round(drift),
        pathname: window.location.pathname,
      });
    }
  }, TICK_MS);
};

const startLongTaskObserver = () => {
  if (typeof PerformanceObserver === "undefined") {
    return;
  }

  // Guard: not every browser supports the 'longtask' entry type.
  const supported =
    PerformanceObserver.supportedEntryTypes &&
    PerformanceObserver.supportedEntryTypes.includes("longtask");
  if (!supported) {
    return;
  }

  let lastReportAt = 0;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration < LONGTASK_REPORT_MS) {
          continue;
        }
        const now = performance.now();
        if (now - lastReportAt < LONGTASK_MIN_GAP_MS) {
          continue;
        }
        lastReportAt = now;

        const attribution = Array.isArray(entry.attribution)
          ? entry.attribution[0]
          : null;

        emitPerformanceEvent("longtask", {
          duration_ms: Math.round(entry.duration),
          pathname: window.location.pathname,
          attribution_name: attribution?.name || null,
          attribution_container: attribution?.containerName || attribution?.containerType || null,
        });
      }
    });
    observer.observe({ type: "longtask", buffered: true });
  } catch {
    // Observer unsupported / failed — drift watchdog still covers us.
  }
};

export const initPerfMonitor = () => {
  if (initialized || typeof window === "undefined" || typeof performance === "undefined") {
    return;
  }
  initialized = true;

  startDriftWatchdog();
  startLongTaskObserver();
};
